import { MonthlyScore } from '../services/types'
import { formatMonth, formatDateTime } from '../utils/formatters'

interface ScoreCardProps {
  score: MonthlyScore
  employeeName?: string
  onViewDetails: (score: MonthlyScore) => void
  onReverse?: (score: MonthlyScore) => void
  canReverse?: boolean
}

export const ScoreCard = ({
  score,
  employeeName,
  onViewDetails,
  onReverse,
  canReverse = false,
}: ScoreCardProps) => {
  const isReversed = score.is_reversed
  const pointsDisplay = score.points_snapshot > 0 ? `+${score.points_snapshot}` : score.points_snapshot

  return (
    <div
      className={`bg-white dark:bg-gray-800 rounded-card border-2 transition-all ${
        isReversed
          ? 'border-gray-300 dark:border-gray-600 opacity-60'
          : 'border-gray-200 dark:border-gray-700'
      }`}
    >
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              {employeeName && (
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {employeeName}
                </h3>
              )}
              {isReversed && (
                <span className="px-2 py-1 text-xs font-medium bg-danger/10 text-danger rounded">
                  REVERTIDO
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {formatMonth(score.month, score.year)}
            </p>
          </div>

          {/* Points Badge */}
          <div
            className={`px-4 py-2 rounded-lg ${
              isReversed
                ? 'bg-gray-100 dark:bg-gray-700'
                : score.points_snapshot > 0
                ? 'bg-success/10'
                : score.points_snapshot < 0
                ? 'bg-danger/10'
                : 'bg-gray-100 dark:bg-gray-700'
            }`}
          >
            <span
              className={`text-2xl font-bold ${
                isReversed
                  ? 'text-gray-500 dark:text-gray-500 line-through'
                  : score.points_snapshot > 0
                  ? 'text-success'
                  : score.points_snapshot < 0
                  ? 'text-danger'
                  : 'text-gray-600 dark:text-gray-400'
              }`}
            >
              {pointsDisplay}
            </span>
          </div>
        </div>

        {/* Observation Preview */}
        {score.observation && (
          <div className="mb-4">
            <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
              "{score.observation}"
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-500">
            {formatDateTime(score.created_at)}
          </div>

          <div className="flex gap-2">
            {/* View Details Button */}
            <button
              onClick={() => onViewDetails(score)}
              className="px-4 py-2 text-sm font-medium text-primary hover:text-primary-hover transition-colors"
            >
              Ver Detalhes
            </button>

            {/* Reverse Button (Admin only) */}
            {canReverse && !isReversed && onReverse && (
              <button
                onClick={() => onReverse(score)}
                className="px-4 py-2 text-sm font-medium text-danger hover:text-danger-hover transition-colors"
              >
                Reverter
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ScoreCard
